// Service for managing candidates using Firebase Firestore
import {
  addCandidate as firestoreAddCandidate,
  getCandidates as firestoreGetCandidates,
  updateCandidate as firestoreUpdateCandidate,
  deleteCandidate as firestoreDeleteCandidate,
  subscribeToCandidates as firestoreSubscribeToCandidates,
  Candidate as FirestoreCandidate
} from '../firebase/firestore';
import { Candidate, CandidateWithMeta } from '../types';
import { onSnapshot, collection, query, orderBy, where, serverTimestamp, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

// Ensure user is authenticated (anonymously) before accessing Firestore
const ensureAuth = async (): Promise<void> => {
  // Skip authentication for now - Firebase rules should be set to allow public access
  console.log('🔓 Skipping Firebase authentication - ensure Firestore rules allow public access');
  return;
};

// CV version type for multi-CV support
export interface CandidateCV {
  id: string;
  company: string;
  template: string;
  originalFileName: string;
  uploadedAt: string;
  extractedFields: {
    personalInfo: any;
    contactInfo: any;
    passport: any;
    education: any;
    experience: any[];
    skills: string[];
    family: any;
    medical: string;
    preferences: any;
  };
  extractionMetadata: {
    timestamp: string;
    confidence: number;
    status: string;
  };
}

// Extended candidate type with CV versions
export interface CandidateWithCVs extends CandidateWithMeta {
  cvs?: CandidateCV[];
  totalCVs?: number;
}

// Export candidate types
export type { Candidate, CandidateWithMeta };

// Subscribe to real-time candidate updates (active candidates only)
export const subscribeToCandidates = async (
  callback: (candidates: CandidateWithMeta[]) => void,
  onError?: (error: any) => void
): Promise<(() => void)> => {
  try {
    await ensureAuth();
    console.log('🔐 Firebase auth confirmed, setting up real-time listener for candidates...');

    // Query for all candidates - we'll filter client-side to avoid index issues
    const q = query(collection(db, "candidates"));

    return onSnapshot(q, (snapshot) => {
      console.log('🔄 Firebase candidates snapshot received, docs count:', snapshot.docs.length);
      const allCandidates = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Processing candidate doc:', doc.id, data.name);
        return {
          id: doc.id,
          _id: doc.id, // Add _id for backward compatibility
          ...data
        } as CandidateWithMeta;
      });

      // Filter out soft-deleted candidates client-side
      const activeCandidates = allCandidates.filter(candidate =>
        !(candidate as any).isDeleted
      );

      // Sort candidates client-side by createdAt (most recent first)
      const sortedCandidates = activeCandidates.sort((a, b) => {
        const aTime = a.createdAt ? (a.createdAt.seconds || new Date(a.createdAt).getTime() / 1000) : 0;
        const bTime = b.createdAt ? (b.createdAt.seconds || new Date(b.createdAt).getTime() / 1000) : 0;
        return bTime - aTime;
      });

      console.log('✅ Processed and sorted active candidates:', sortedCandidates.length);
      callback(sortedCandidates);
    }, (error) => {
      console.error('❌ Firebase onSnapshot error for candidates:', error);
      if (onError) {
        onError(error);
      }
    });
  } catch (error) {
    console.error('❌ Failed to set up Firebase candidates subscription:', error);
    if (onError) {
      onError(error);
    }
    // Return a no-op unsubscribe function
    return () => { };
  }
};

// Subscribe to deleted candidates for the Recycle Bin
export const getDeletedCandidates = async (
  callback: (candidates: CandidateWithMeta[]) => void,
  onError?: (error: any) => void
): Promise<(() => void)> => {
  try {
    await ensureAuth();
    console.log('🗑️ Setting up real-time listener for deleted candidates...');

    // Query for all candidates - we'll filter for deleted ones client-side to avoid index issues
    const q = query(collection(db, "candidates"));

    return onSnapshot(q, (snapshot) => {
      console.log('🗑️ Firebase deleted candidates snapshot received, docs count:', snapshot.docs.length);
      const allCandidates = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          _id: doc.id, // Add _id for backward compatibility
          ...data
        } as CandidateWithMeta;
      });

      // Filter for soft-deleted candidates client-side
      const deletedCandidates = allCandidates.filter(candidate =>
        (candidate as any).isDeleted === true
      );

      console.log('🗑️ Processing', deletedCandidates.length, 'deleted candidate docs');

      // Sort deleted candidates client-side by deletedAt (most recent first)
      const sortedDeletedCandidates = deletedCandidates.sort((a, b) => {
        const aTime = (a as any).deletedAt ? ((a as any).deletedAt.seconds || new Date((a as any).deletedAt).getTime() / 1000) : 0;
        const bTime = (b as any).deletedAt ? ((b as any).deletedAt.seconds || new Date((b as any).deletedAt).getTime() / 1000) : 0;
        return bTime - aTime;
      });

      console.log('✅ Processed and sorted deleted candidates:', sortedDeletedCandidates.length);
      callback(sortedDeletedCandidates);
    }, (error) => {
      console.error('❌ Firebase onSnapshot error for deleted candidates:', error);
      if (onError) {
        onError(error);
      }
    });
  } catch (error) {
    console.error('❌ Failed to set up Firebase deleted candidates subscription:', error);
    if (onError) {
      onError(error);
    }
    // Return a no-op unsubscribe function
    return () => { };
  }
};

// Add new candidate
export const addCandidate = async (candidate: Omit<Candidate, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    await ensureAuth();
    console.log('📝 Adding candidate to Firebase:', candidate.name);
    console.log('📝 Candidate data:', JSON.stringify(candidate, null, 2));

    // Ensure all required fields have values
    const candidateData = {
      name: candidate.name || 'Unknown',
      email: candidate.email || `${Date.now()}@temp.com`,
      phone: candidate.phone || '+000-000-0000',
      stage: candidate.stage || 'Onboarding',
      jobTitle: candidate.jobTitle || '',
      company: candidate.company || '',
      salary: candidate.salary || 0,
      city: candidate.city || '',
      country: candidate.country || '',
      countryApplyingTo: candidate.countryApplyingTo || '',
      passportNumber: candidate.passportNumber || '',
      notes: candidate.notes || '',
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('📝 Processed candidate data:', candidateData);

    const id = await firestoreAddCandidate(candidateData);
    console.log('✅ Candidate added to Firebase with ID:', id);
    return id;
  } catch (error: any) {
    console.error('❌ Failed to add candidate:', error);
    console.error('❌ Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Firebase security rules may be blocking writes. Please contact support.');
    }
    
    throw error;
  }
};

// Update candidate
export const updateCandidate = async (id: string, updates: Partial<Candidate>): Promise<void> => {
  await ensureAuth();
  console.log('📝 Updating candidate in Firebase:', id);

  const updateData = {
    ...updates,
    updatedAt: serverTimestamp()
  };

  await firestoreUpdateCandidate(id, updateData);
  console.log('✅ Candidate updated in Firebase:', id);
};

// Soft delete candidate (move to recycle bin)
export const softDeleteCandidate = async (id: string): Promise<void> => {
  await ensureAuth();
  console.log('🗑️ Soft deleting candidate from Firebase:', id);

  await firestoreUpdateCandidate(id, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  console.log('✅ Candidate soft deleted in Firebase:', id);
};

// Restore candidate from recycle bin
export const restoreCandidate = async (id: string): Promise<void> => {
  await ensureAuth();
  console.log('♻️ Restoring candidate from recycle bin:', id);

  await firestoreUpdateCandidate(id, {
    isDeleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp()
  });
  console.log('✅ Candidate restored in Firebase:', id);
};

// Permanently delete candidate
export const permanentlyDeleteCandidate = async (id: string): Promise<void> => {
  await ensureAuth();
  console.log('🗑️ Permanently deleting candidate from Firebase:', id);

  await firestoreDeleteCandidate(id);
  console.log('✅ Candidate permanently deleted from Firebase:', id);
};

// Get all candidates (one-time fetch)
export const getAllCandidates = async (): Promise<CandidateWithMeta[]> => {
  await ensureAuth();
  const result = await firestoreGetCandidates();
  return result.candidates
    .filter(candidate => !(candidate as any).isDeleted)
    .map(candidate => ({
      ...candidate,
      _id: candidate.id // Add _id for backward compatibility
    } as CandidateWithMeta));
};

// Update candidate stage
export const updateCandidateStage = async (id: string, stage: string): Promise<void> => {
  await updateCandidate(id, { stage });
};

// Get all CV versions for a candidate
export const getCandidateCVs = async (candidateId: string): Promise<CandidateCV[]> => {
  try {
    await ensureAuth();
    console.log('📄 Fetching CV versions for candidate:', candidateId);
    
    const cvsCollectionRef = collection(db, "candidates", candidateId, "cvs");
    const cvsQuery = query(cvsCollectionRef, orderBy("uploadedAt", "desc"));
    const snapshot = await getDocs(cvsQuery);
    
    const cvs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CandidateCV));
    
    console.log('✅ Retrieved', cvs.length, 'CV versions for candidate:', candidateId);
    return cvs;
  } catch (error) {
    console.error('❌ Failed to get candidate CVs:', error);
    return [];
  }
};

// Get candidate with all CV versions
export const getCandidateWithCVs = async (candidateId: string): Promise<CandidateWithCVs | null> => {
  try {
    await ensureAuth();
    console.log('📄 Fetching candidate with CVs:', candidateId);
    
    // Get candidate document
    const candidateDoc = await getDoc(doc(db, "candidates", candidateId));
    if (!candidateDoc.exists()) {
      console.log('❌ Candidate not found:', candidateId);
      return null;
    }
    
    const candidateData = {
      id: candidateDoc.id,
      _id: candidateDoc.id,
      ...candidateDoc.data()
    } as CandidateWithMeta;
    
    // Get all CV versions
    const cvs = await getCandidateCVs(candidateId);
    
    const candidateWithCVs: CandidateWithCVs = {
      ...candidateData,
      cvs: cvs,
      totalCVs: cvs.length
    };
    
    console.log('✅ Retrieved candidate with', cvs.length, 'CV versions');
    return candidateWithCVs;
  } catch (error) {
    console.error('❌ Failed to get candidate with CVs:', error);
    return null;
  }
};

// Find candidate by passport number
export const findCandidateByPassport = async (passportNumber: string): Promise<CandidateWithMeta | null> => {
  try {
    await ensureAuth();
    console.log('🔍 Searching candidate by passport:', passportNumber);
    
    const candidatesRef = collection(db, "candidates");
    const q = query(candidatesRef, where("passportNumber", "==", passportNumber));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log('❌ No candidate found with passport:', passportNumber);
      return null;
    }
    
    const doc = snapshot.docs[0];
    const candidate = {
      id: doc.id,
      _id: doc.id,
      ...doc.data()
    } as CandidateWithMeta;
    
    console.log('✅ Found candidate by passport:', candidate.name);
    return candidate;
  } catch (error) {
    console.error('❌ Failed to find candidate by passport:', error);
    return null;
  }
};

// Subscribe to CV versions for a candidate
export const subscribeToCandidateCVs = async (
  candidateId: string,
  callback: (cvs: CandidateCV[]) => void,
  onError?: (error: any) => void
): Promise<(() => void)> => {
  try {
    await ensureAuth();
    console.log('🔄 Setting up real-time listener for candidate CVs:', candidateId);
    
    const cvsCollectionRef = collection(db, "candidates", candidateId, "cvs");
    const cvsQuery = query(cvsCollectionRef, orderBy("uploadedAt", "desc"));
    
    return onSnapshot(cvsQuery, (snapshot) => {
      console.log('🔄 CVs snapshot received, docs count:', snapshot.docs.length);
      const cvs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as CandidateCV));
      
      callback(cvs);
    }, (error) => {
      console.error('❌ Firebase onSnapshot error for CVs:', error);
      if (onError) {
        onError(error);
      }
    });
  } catch (error) {
    console.error('❌ Failed to set up Firebase CVs subscription:', error);
    if (onError) {
      onError(error);
    }
    // Return a no-op unsubscribe function
    return () => { };
  }
};